/**
 * Asks the Radio Browser directory (radio-browser.info) for the stations
 * around a place: those within a drive of it, and the country's most
 * listened to. Its servers are mirrors of each other; one that does not
 * answer is passed over for the next. See `core/audio/radio.ts`.
 */

import type { DirectoryStation, Station } from '../core/audio/radio.js';
import { LOCAL_KM, directoryCode, pickStations } from '../core/audio/radio.js';
import type { Country } from '../core/geo/country.js';

const MIRRORS = ['de1', 'de2', 'fi1', 'nl1'].map(m => `https://${m}.api.radio-browser.info/json`);
const TIMEOUT_MS = 8000;
/** Where the country is not known, how far off its stations are still "the country's", kilometres. */
const REGION_KM = 400;

async function ask(query: string): Promise<DirectoryStation[]> {
  for (const base of MIRRORS) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(`${base}/stations/search?${query}&hidebroken=true&is_https=true&order=clickcount&reverse=true`, { signal: controller.signal });
      if (response.ok) return await response.json() as DirectoryStation[];
    } catch {
      // This mirror is down or slow: the next.
    } finally {
      clearTimeout(timer);
    }
  }
  return [];
}

/** The stations to tune through at (lat, lon), in `country` where known. Empty where the directory has none, or does not answer. */
export async function stationsNear(lat: number, lon: number, country: Country | undefined): Promise<Station[]> {
  const at = `geo_lat=${lat.toFixed(4)}&geo_long=${lon.toFixed(4)}`;
  const [near, wide] = await Promise.all([
    ask(`${at}&geo_distance=${LOCAL_KM * 1000}&limit=40`),
    ask(country !== undefined ? `countrycode=${directoryCode(country)}&limit=60` : `${at}&geo_distance=${REGION_KM * 1000}&limit=60`),
  ]);
  return pickStations([...near, ...wide], lat, lon);
}
